import { User } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import PageLoader from "../components/PageLoader";
import StatBox from "../components/league/StatBox";
import { fetchMyLeagues, LeagueSummary } from "../api/leagues";
import { fetchArchives, LeagueArchiveSummary } from "../api/archives";
import { fetchMyStats, fetchMe, UserStats } from "../api/users";
import { card, primaryBtn } from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";
import { MODE_CONTENT, MODE_COLOR_VAR } from "./createLeague/modeContent";
import { hasSeenGwRecap } from "../lib/gwRecapSeen";

interface Props {
  user: User;
  displayName: string;
  onActiveLeagueCountChange?: (count: number) => void;
}

const COLS_DESKTOP = "24px 1fr 150px 20px";
const COLS_MOBILE  = "32px 1fr auto 16px";

const headerCell: React.CSSProperties = {
  fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.1em", color: "var(--color-text-dim)",
};

export function LeagueTableHeader({ isMobile = false }: { isMobile?: boolean }) {
  const { t } = useTranslation();
  const cols = isMobile ? COLS_MOBILE : COLS_DESKTOP;
  return (
    <div style={{ display: "grid", gridTemplateColumns: cols, columnGap: "0.75rem", padding: "0.5rem 1.25rem", borderBottom: "1px solid var(--color-border)" }}>
      <span />
      <span style={headerCell}>{t("overview.table.league")}</span>
      {!isMobile && <span style={headerCell}>{t("overview.table.mode")}</span>}
      {isMobile && <span />}
      <span />
    </div>
  );
}

// ── League row ─────────────────────────────────────────────────────────────────

function LeagueRow({ league, uid, isMobile = false }: { league: LeagueSummary; uid: string; isMobile?: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cols = isMobile ? COLS_MOBILE : COLS_DESKTOP;
  const Icon = MODE_CONTENT[league.mode].icon;
  const modeColor = MODE_COLOR_VAR[league.mode];
  const modeLabel = t(`createLeague.mode.${league.mode}.label`);
  const hasUnseenRecap = league.latestCompletedGwNumber != null && !hasSeenGwRecap(uid, league.id, league.latestCompletedGwNumber);

  return (
    <button
      onClick={() => navigate(`/leagues/${league.id}`)}
      style={{
        display: "grid", gridTemplateColumns: cols, columnGap: "0.75rem",
        alignItems: "center", width: "100%", background: "transparent",
        border: "none", cursor: "pointer", padding: "0.75rem 1.25rem",
        textAlign: "left", transition: "background 0.1s",
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-surface-hover)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
    >
      {/* Mode icon — tinted badge on mobile, bare colour on desktop */}
      {isMobile ? (
        <span style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `color-mix(in srgb, ${modeColor} 16%, transparent)`,
          color: modeColor,
        }}>
          <span style={{ display: "inline-flex", transform: "scale(0.69)" }}><Icon /></span>
        </span>
      ) : (
        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", color: modeColor }}>
          <span style={{ display: "inline-flex", transform: "scale(0.615)" }}><Icon /></span>
        </span>
      )}

      {/* League name + optional unseen-recap dot + optional Finished badge */}
      <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", overflow: "hidden" }}>
        <span style={{ fontSize: "0.88rem", fontWeight: 400, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {league.name}
        </span>
        {hasUnseenRecap && (
          <span
            title={t("gwRecap.reopen")}
            style={{ flexShrink: 0, width: 7, height: 7, borderRadius: "50%", background: "var(--color-accent)", boxShadow: "0 0 5px var(--color-accent)" }}
          />
        )}
        {league.status === "COMPLETED" && !league.archived && (
          <span style={{
            fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
            background: "var(--color-surface-alt)", color: "var(--color-text-muted)",
            border: "1px solid var(--color-border)", borderRadius: 4, padding: "1px 5px", flexShrink: 0,
          }}>
            {t("overview.finishedBadge")}
          </span>
        )}
      </span>

      {/* Mode badge — coloured pill, both mobile and desktop */}
      <span style={{
        display: "inline-flex", alignItems: "center", justifySelf: isMobile ? "end" : "start",
        fontSize: "0.68rem", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
        padding: "2px 8px", borderRadius: 6,
        border: `1px solid ${modeColor}`,
        background: `color-mix(in srgb, ${modeColor} 12%, transparent)`,
        color: modeColor,
      }}>
        {modeLabel}
      </span>

      {/* Chevron */}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-dim)" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}


// ── Archive row ────────────────────────────────────────────────────────────────
// Archived leagues have no live scoring left to fetch — clicking through goes to
// a dedicated page (ArchivePage) built entirely from the frozen standings payload.
// No mode icon: modeLabel is a plain string with no matching entry in MODE_CONTENT
// (deliberately — a retired mode needs no lookup here).

function ArchiveRow({ archive, isMobile = false }: { archive: LeagueArchiveSummary; isMobile?: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cols = isMobile ? COLS_MOBILE : COLS_DESKTOP;

  return (
    <button
      onClick={() => navigate(`/archives/${archive.id}`)}
      style={{
        display: "grid", gridTemplateColumns: cols, columnGap: "0.75rem",
        alignItems: "center", width: "100%", background: "transparent",
        border: "none", cursor: "pointer", padding: "0.75rem 1.25rem",
        textAlign: "left", transition: "background 0.1s",
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-surface-hover)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-dim)" }}>
        <span style={{ fontSize: isMobile ? "1rem" : "0.9rem" }}>🏆</span>
      </span>

      <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", overflow: "hidden" }}>
        <span style={{ fontSize: "0.88rem", fontWeight: 400, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {archive.name}
        </span>
        <span style={{
          fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
          background: "var(--color-surface-alt)", color: "var(--color-text-muted)",
          border: "1px solid var(--color-border)", borderRadius: 4, padding: "1px 5px", flexShrink: 0,
        }}>
          {t("overview.finishedBadge")}
        </span>
      </span>

      <span style={{
        display: "inline-flex", alignItems: "center", justifySelf: isMobile ? "end" : "start",
        fontSize: "0.68rem", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
        padding: "2px 8px", borderRadius: 6,
        border: "1px solid var(--color-text-dim)",
        color: "var(--color-text-dim)",
      }}>
        {archive.modeLabel}
      </span>

      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-dim)" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}

type ViewFilter = "active" | "completed" | "all";


// ── Page ───────────────────────────────────────────────────────────────────────

export default function HomePage({ user, displayName, onActiveLeagueCountChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [leagues, setLeagues] = useState<LeagueSummary[]>([]);
  const [archives, setArchives] = useState<LeagueArchiveSummary[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(false);
  const delayedLoading = useDelayedLoading(loading);
  const [error, setError] = useState("");
  const [view, setView] = useState<ViewFilter>("active");

  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([
      fetchMyLeagues(user),
      fetchArchives(user).catch(() => []),
      fetchMyStats(user).catch(() => null),
      fetchMe(user).catch(() => null),
    ])
      .then(([leagueData, archiveData, statsData]) => {
        setLeagues(leagueData);
        setArchives(archiveData);
        setStats(statsData);
        const active = leagueData.filter(
          (l) => l.membershipStatus === "APPROVED" && l.status === "RUNNING",
        ).length;
        onActiveLeagueCountChange?.(active);
      })
      .catch((err) => {
        console.error("Failed to load home data:", err);
        setError(t("overview.errors.failedToLoad"));
        setLeagues([]);
      })
      .finally(() => setLoading(false));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (delayedLoading) return <PageLoader />;
  if (loading) return null;

  type Row =
    | { kind: "league"; id: string; season: string; league: LeagueSummary }
    | { kind: "archive"; id: string; season: string; archive: LeagueArchiveSummary };

  const approved = leagues.filter((l) => l.membershipStatus === "APPROVED");
  // Archives are always historical — they never appear under "active".
  const activeLeagues: Row[] = approved
    .filter((l) => !l.archived)
    .map((l): Row => ({ kind: "league", id: l.id, season: l.competition.season, league: l }));
  const completedLeagues: Row[] = [
    ...approved.filter((l) => l.archived).map((l): Row => ({ kind: "league", id: l.id, season: l.competition.season, league: l })),
    ...archives.map((a): Row => ({ kind: "archive", id: a.id, season: a.season, archive: a })),
  ];
  const allRows: Row[] = [...activeLeagues, ...completedLeagues];

  const filtered = view === "active" ? activeLeagues : view === "completed" ? completedLeagues : allRows;

  // Group by season, sorted newest first
  const seasonMap = new Map<string, Row[]>();
  for (const row of filtered) {
    if (!seasonMap.has(row.season)) seasonMap.set(row.season, []);
    seasonMap.get(row.season)!.push(row);
  }
  const seasons = Array.from(seasonMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));

  const pills: { key: ViewFilter; label: string; count: number }[] = [
    { key: "active",    label: t("overview.filters.active"),    count: activeLeagues.length },
    { key: "completed", label: t("overview.filters.completed"), count: completedLeagues.length },
    { key: "all",       label: t("overview.filters.all"),       count: allRows.length },
  ];

  const getStartedOptions = [
    { title: t("overview.getStarted.create.title"), desc: t("overview.getStarted.create.desc"), cta: `${t("overview.getStarted.create.cta")} →`, to: "/leagues/create" },
    { title: t("overview.getStarted.join.title"), desc: t("overview.getStarted.join.desc"), cta: `${t("overview.getStarted.join.cta")} →`, to: "/leagues/join" },
  ];

  const sectionLabel: React.CSSProperties = {
    fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase" as const,
    letterSpacing: "0.1em", color: "var(--color-text-dim)",
  };

  return (
    <div className="sg-fade-in" style={{ maxWidth: 860, margin: "0 auto", padding: isMobile ? "1rem 0" : "1.5rem 0" }}>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      {/* ── Header card ── */}
      <div style={{ ...card, marginBottom: "1.25rem" }}>
        {/* Metadata row — matches league page */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.6rem" }}>
          <span style={{ ...sectionLabel }}>{t("overview.eyebrow")}</span>
        </div>

        {/* Title row */}
        <div style={{ marginBottom: stats ? "1.25rem" : 0 }}>
          <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: isMobile ? "1.6rem" : "2rem", fontWeight: 800, letterSpacing: "0.01em", color: "var(--color-text)", lineHeight: 1.1 }}>
            {t("overview.greeting", { name: displayName || t("overview.greetingFallback") })}
          </h1>
        </div>

        {/* Stat boxes — uses the same StatBox component as league pages */}
        {stats && (
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <StatBox label={t("overview.stats.active")} value={stats.activeLeaguesCount} color="var(--color-accent)" />
            <StatBox label={t("overview.stats.completed")} value={stats.completedLeaguesCount} color="var(--color-mode-gameweek-challenge)" />
            <StatBox label={t("overview.stats.topThree")} value={stats.topThreeCount} color="var(--color-accent-2)" />
            <StatBox
              label={t("overview.stats.winRate")}
              value={stats.completedLeaguesCount > 0 ? `${Math.round(stats.winRate * 100)}%` : "—"}
              sub={t("overview.stats.victories", { count: stats.victoriesCount })}
              color="var(--color-success)"
              showSubOnMobile
            />
          </div>
        )}
      </div>

      {/* ── Get started — two independent cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
        {getStartedOptions.map((opt, i) => (
          <div key={opt.to} style={{ ...card, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {i === 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                <span style={{ ...sectionLabel }}>{t("overview.getStarted.eyebrow")}</span>
              </div>
            )}
            <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--color-text)" }}>{opt.title}</span>
            <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-muted)", lineHeight: 1.4, flex: 1 }}>{opt.desc}</p>
            <button onClick={() => navigate(opt.to)} style={{ ...primaryBtn, alignSelf: "flex-start", fontSize: "0.8rem" }}>{opt.cta}</button>
          </div>
        ))}
      </div>

      {/* ── League list card ── */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        {/* Card header: filter pills */}
        <div style={{ padding: "0.85rem 1.25rem", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
          {pills.map(({ key, label, count }) => {
            const active = view === key;
            return (
              <button
                key={key}
                onClick={() => setView(key)}
                style={{
                  padding: "0.25rem 0.75rem", borderRadius: 999, fontSize: "0.75rem", fontWeight: 600,
                  border: "1px solid var(--color-border)", cursor: "pointer",
                  background: active ? "var(--color-accent)" : "transparent",
                  color: active ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
                  display: "flex", alignItems: "center", gap: "0.35rem",
                }}
              >
                {label}
                <span style={{
                  fontSize: "0.65rem", fontWeight: 700,
                  background: active ? "rgba(255,255,255,0.2)" : "var(--color-surface-alt)",
                  borderRadius: 10, padding: "1px 5px",
                  color: active ? "white" : "var(--color-text-dim)",
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Column headers */}
        {seasons.length > 0 && <LeagueTableHeader isMobile={isMobile} />}

        {/* Season groups */}
        {seasons.length === 0 ? (
          <div style={{ padding: isMobile ? "1.75rem 1.25rem" : "2.25rem 1.75rem", textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            {t("overview.emptyState")}
          </div>
        ) : (
          seasons.map(([season, ls], si) => (
            <div key={season} style={{ borderTop: si > 0 ? "1px solid var(--color-border)" : "none" }}>
              {/* Season header */}
              <div style={{ padding: "0.6rem 1.25rem 0", display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{season}</span>
                <span style={{ fontSize: "0.65rem", color: "var(--color-text-dim)" }}>{ls.length}</span>
              </div>
              {/* Rows */}
              {ls.map((row, i) => (
                <div key={row.id} style={{ borderTop: i > 0 ? "1px solid var(--color-border-subtle)" : "none" }}>
                  {row.kind === "league"
                    ? <LeagueRow league={row.league} uid={user.uid} isMobile={isMobile} />
                    : <ArchiveRow archive={row.archive} isMobile={isMobile} />}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
